import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { colors } from '../../styles/colors';
import { useProjetsManagement } from '../../hooks/useProjetsManagement';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';

const thStyle = { padding: '0.875rem 1rem', fontSize: '0.875rem', fontWeight: '600', color: colors.text.white, textAlign: 'left' };
const tdStyle = { padding: '0.875rem 1rem', color: colors.primary.gray };

const inputStyle = {
  padding: '0.75rem',
  border: `1px solid ${colors.secondary.lightGray}`,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  fontFamily: '"Noto Sans", sans-serif',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', marginBottom: '0.4rem',
  fontSize: '0.875rem', fontWeight: '500',
  color: colors.primary.gray, fontFamily: '"Noto Sans", sans-serif',
};

const actionBtn = (bg) => ({
  padding: '0.4rem', backgroundColor: bg, color: colors.text.white,
  border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
  display: 'flex', alignItems: 'center', transition: 'opacity 150ms',
});

function StatutBadge({ statut }) {
  const actif = !statut || statut === 'Actif';
  return (
    <span style={{
      padding: '0.2rem 0.65rem', borderRadius: '1rem',
      fontSize: '0.8125rem', fontWeight: '500',
      backgroundColor: actif ? colors.background.paleGreen : colors.background.lightRed,
      color: actif ? colors.text.darkGreen2 : colors.text.darkRed,
    }}>
      {statut || 'Actif'}
    </span>
  );
}

function ProjetsTab() {
  const {
    filteredProjets, clients, loading, error,
    searchTerm, setSearchTerm,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    getClientNom,
  } = useProjetsManagement();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div style={{ width: '40px', height: '40px', border: `4px solid ${colors.secondary.lightTeal}`, borderTop: `4px solid ${colors.primary.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ ...inputStyle, flex: 1, maxWidth: '360px' }}
        />
        <button
          onClick={() => handleOpenModal('create')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.75rem 1.25rem',
            backgroundColor: colors.primary.teal, color: colors.text.white,
            border: 'none', borderRadius: '0.5rem',
            fontSize: '0.9375rem', fontWeight: '500', cursor: 'pointer',
            fontFamily: '"Noto Sans", sans-serif',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f6a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary.teal}
        >
          <Plus size={18} /> Ajouter
        </button>
      </div>

      {error && (
        <div style={{ backgroundColor: colors.background.lightRed, border: `1px solid ${colors.status.error}`, color: colors.text.darkRed, padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: '"Noto Sans", sans-serif' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '"Noto Sans", sans-serif' }}>
          <thead>
            <tr style={{ backgroundColor: colors.primary.gray }}>
              <th style={thStyle}>Nom du projet</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              <th style={thStyle}>Client final</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjets.map((p, i) => (
              <tr key={p.id} style={{ backgroundColor: i % 2 === 0 ? colors.text.white : colors.secondary.lightGray, borderBottom: `1px solid ${colors.secondary.lightGray}` }}>
                <td style={{ ...tdStyle, fontWeight: '500' }}>{p.nom}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}><StatutBadge statut={p.statut} /></td>
                <td style={{ ...tdStyle, color: colors.secondary.mediumGray, fontStyle: p.client_id ? 'normal' : 'italic' }}>
                  {getClientNom(p.client_id)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                    <button onClick={() => handleOpenModal('edit', p)} style={actionBtn(colors.secondary.lightBlue)} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'} title="Modifier"><Edit2 size={15} /></button>
                    <button onClick={() => handleDeleteClick(p)} style={actionBtn(colors.status.error)} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'} title="Supprimer"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredProjets.length === 0 && (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: colors.secondary.mediumGray, fontFamily: '"Noto Sans", sans-serif' }}>
            Aucun projet trouvé
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', padding: '2rem', width: '90%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif', fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary.gray, margin: 0 }}>
                {modalMode === 'create' ? 'Ajouter un projet' : 'Modifier le projet'}
              </h2>
              <button onClick={handleCloseModal} style={{ padding: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.secondary.mediumGray }}><X size={22} /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Nom du projet *</label>
                  <input type="text" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Statut</label>
                  <select value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', backgroundColor: colors.text.white }}>
                    <option value="Actif">Actif</option>
                    <option value="Inactif">Inactif</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Client final</label>
                  <select value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', backgroundColor: colors.text.white }}>
                    <option value="">— Aucun —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button type="button" onClick={handleCloseModal} style={{ flex: 1, padding: '0.75rem', backgroundColor: colors.secondary.lightGray, color: colors.primary.gray, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={submitting} style={{ flex: 1, padding: '0.75rem', backgroundColor: submitting ? colors.secondary.mediumGray : colors.primary.teal, color: colors.text.white, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '500', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    {submitting ? 'Enregistrement...' : (modalMode === 'create' ? 'Créer' : 'Modifier')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmModal isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDeleteConfirm} loading={deleting}>
        {itemToDelete && (
          <p style={{ margin: 0 }}>
            Supprimer le projet <strong>{itemToDelete.nom}</strong> ?<br />
            <span style={{ color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>Cette action est irréversible.</span>
          </p>
        )}
      </DeleteConfirmModal>
    </div>
  );
}

export default ProjetsTab;
